import React, { useState, useEffect } from "react";
import { fetchData } from "../../utils/functions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";

const EditHouseholdDeath = ({ 
  mortalityData = {}, 
  householdListings = [],
  DqaApproved = false,
  DqaRejected = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [mortality, setMortality] = useState({
    anyDeath: "",
    deathCount: "",
    demisedPersons: [],
  });

  const deathCountOptions = [
    { label: "One", value: "One" },
    { label: "Two", value: "Two" },
    { label: "Three", value: "Three" },
    { label: "Four and above", value: "Four and above" },
  ];

  const vaccinationOptions = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [mortalityRecord, setMortalityRecord] = useState(null);
  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved);
  const [isDqaRejected, setIsDqaRejected] = useState(DqaRejected);

  // Check if there's any mortality data
  const hasMortalityData =
    mortalityData && Object.keys(mortalityData).length > 0;

  const [errors, setErrors] = useState({});

  // Helper function to convert death count label to number
  const getDeathCountNumber = (deathCountValue) => {
    const countMap = {
      One: 1,
      Two: 2,
      Three: 3,
      "Four and above": 4,
    };
    return countMap[deathCountValue] || 0;
  };

  // Update isDqaApproved when prop changes
  useEffect(() => {
    setIsDqaApproved(DqaApproved);
    setIsDqaRejected(DqaRejected);
  }, [DqaApproved, DqaRejected]);

  useEffect(() => {
    if (hasMortalityData) {
      setMortalityRecord(mortalityData);
      const deathCountValue = mortalityData.death_counts?.toString() || "";
      const count = getDeathCountNumber(deathCountValue);

      const demisedPersons = [];

      if (count > 0) {
        const existingGender = mortalityData.demised_gender || "";
        const existingMaternalDeath = mortalityData.female_maternal_death || "";
        const existingAge = mortalityData.age_on_death?.toString() || "";

        for (let i = 0; i < count; i++) {
          let personGender = "";
          let personMaternalDeath = "";
          let personAge = existingAge;

          if (existingGender === "Both") {
            personGender = i % 2 === 0 ? "Male" : "Female";
          } else if (existingGender === "Male" || existingGender === "Female") {
            personGender = existingGender;
          } else {
            const genderParts = existingGender
              .split(",")
              .map((g) => g.trim())
              .filter(Boolean);
            personGender = genderParts[i] || genderParts[0] || "";
          }

          if (personGender === "Female") {
            personMaternalDeath =
              existingMaternalDeath === "yes" ||
              existingMaternalDeath === "1" ||
              existingMaternalDeath === 1
                ? "yes"
                : "no";
          } else {
            personMaternalDeath = "";
          }
          const ageParts = existingAge
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean);
          personAge = ageParts[i] || ageParts[0] || "";

          demisedPersons.push({
            gender: personGender,
            maternalDeath: personMaternalDeath,
            ageOnDeath: personAge,
          });
        }
      }

      setMortality({
        anyDeath:
          mortalityData.death_in_last_5_years === 1 ||
          mortalityData.death_in_last_5_years === true
            ? "yes"
            : "no",
        deathCount: deathCountValue,
        demisedPersons: demisedPersons,
      });
    } else {
      // No mortality data
      setMortalityRecord(null);
      setMortality({
        anyDeath: "",
        deathCount: "",
        demisedPersons: [],
      });
    }
    setLoading(false);
  }, [mortalityData, householdListings, hasMortalityData]);

  const setField = (field, value) => {
    setMortality((prev) => {
      let next = { ...prev, [field]: value };

      // dependency logic
      if (field === "anyDeath") {
        if (value === "no") {
          next = {
            ...next,
            deathCount: "",
            demisedPersons: [],
          };
          setErrors({});
        }
      }

      if (field === "deathCount") {
        const count = getDeathCountNumber(value);
        const currentPersons = prev.demisedPersons || [];
        const newPersons = [];

        // Preserve existing data when possible
        for (let i = 0; i < count; i++) {
          if (currentPersons[i]) {
            // Keep existing person data
            newPersons.push({ ...currentPersons[i] });
          } else {
            // Add new empty person
            newPersons.push({
              gender: "",
              maternalDeath: "",
              ageOnDeath: "",
            });
          }
        }
        next.demisedPersons = newPersons;
      }

      return next;
    });
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const setPersonField = (personIndex, field, value) => {
    setMortality((prev) => {
      const newPersons = [...prev.demisedPersons];

      // Ensure the person object exists
      if (!newPersons[personIndex]) {
        newPersons[personIndex] = {
          gender: "",
          maternalDeath: "",
          ageOnDeath: "",
        };
      }

      if (field === "gender") {
        newPersons[personIndex] = {
          ...newPersons[personIndex],
          gender: value,
        };
        // Clear maternal death if male selected
        if (value !== "Female") {
          newPersons[personIndex].maternalDeath = "";
        }
      } else {
        newPersons[personIndex] = {
          ...newPersons[personIndex],
          [field]: value,
        };
      }
      return { ...prev, demisedPersons: newPersons };
    });

    // Clear person-specific errors
    setErrors((e) => {
      const newErrors = { ...e };
      if (newErrors.demisedPersons && newErrors.demisedPersons[personIndex]) {
        delete newErrors.demisedPersons[personIndex][field];
      }
      return newErrors;
    });
  };

  const isInt = (v) => /^-?\d+$/.test(String(v).trim());
  const inRange = (n, min, max) => n >= min && n <= max;
  const required = (v) =>
    v !== null && v !== undefined && String(v).trim() !== "";

  const validate = () => {
    const e = {};
    // anyDeath required
    if (!required(mortality.anyDeath)) {
      e.anyDeath = "Please select Yes or No.";
    }

    if (mortality.anyDeath === "yes") {
      // deathCount
      if (!required(mortality.deathCount)) {
        e.deathCount = "Required.";
      }

      // Validate each person
      const personErrors = [];
      mortality.demisedPersons.forEach((person, index) => {
        const personError = {};

        // Gender must be selected
        if (!person.gender) {
          personError.gender = "Please select a gender.";
        }

        // Maternal death required only if female is selected
        if (person.gender === "Female") {
          if (!required(person.maternalDeath)) {
            personError.maternalDeath = "Please select Yes or No.";
          }
        }

        // Age on death
        if (!required(person.ageOnDeath)) {
          personError.ageOnDeath = "Required.";
        } else if (
          !isInt(person.ageOnDeath) ||
          !inRange(Number(person.ageOnDeath), 0, 120)
        ) {
          personError.ageOnDeath = "Enter a whole number between 0 and 120.";
        }

        if (Object.keys(personError).length > 0) {
          personErrors[index] = personError;
        }
      });

      if (personErrors.length > 0) {
        e.demisedPersons = personErrors;
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!mortalityRecord || !mortalityRecord.household_death_id) {
      toast.error("No existing mortality record found to update");
      return;
    }

    if (!validate()) {
      return;
    }

    try {
      setUpdating(true);

      // Prepare data for submission
      let demisedGender = "";
      let femaleMaternaldeath = "no";
      let ageOnDeath = "";

      if (mortality.demisedPersons.length > 0) {
        // Collect all genders
        const genders = mortality.demisedPersons
          .map((person) => person.gender)
          .filter(Boolean);
        const uniqueGenders = [...new Set(genders)];

        // Set demised gender
        if (
          uniqueGenders.includes("Male") &&
          uniqueGenders.includes("Female")
        ) {
          demisedGender = "Both";
        } else if (uniqueGenders.includes("Male")) {
          demisedGender = "Male";
        } else if (uniqueGenders.includes("Female")) {
          demisedGender = "Female";
        }

        // Check for maternal death
        const hasMaternalDeath = mortality.demisedPersons.some(
          (person) =>
            person.gender === "Female" && person.maternalDeath === "yes"
        );
        femaleMaternaldeath = hasMaternalDeath ? "yes" : "no";

        // Collect ages (could be multiple)
        const ages = mortality.demisedPersons
          .map((person) => person.ageOnDeath)
          .filter(Boolean);
        ageOnDeath = ages.join(","); // Store as comma-separated if multiple
      }

      const payload = {
        householdDeath: {
          household_information_id: parseInt(id),
          death_in_last_5_years: mortality.anyDeath === "yes" ? 1 : 0,
          death_counts:
            mortality.anyDeath === "yes" ? mortality.deathCount || null : null,
          demised_gender:
            mortality.anyDeath === "yes" ? demisedGender || null : null,
          female_maternal_death:
            mortality.anyDeath === "yes" ? femaleMaternaldeath : null,
          age_on_death:
            mortality.anyDeath === "yes" ? ageOnDeath || null : null,
          status: 1,
        },
      };

      const response = await fetchData(
        `household-death-update/${mortalityRecord.household_death_id}`,
        "PUT",
        payload,
        {},
        {},
        navigate,
        "/household",
        dispatch
      );

      if (!response?.success) {
        throw new Error(response?.message || "Failed to update mortality data");
      }

      toast.success("Mortality data updated successfully");
    } catch (error) {
      console.error("Error updating mortality data:", error);
      toast.error(error.message || "Error updating mortality data");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">
          Loading mortality information...
        </div>
      </div>
    );
  }

  // If no mortality data, show message similar to Debt component
  if (!hasMortalityData) {
    return (
      <div className="bg-white">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            {isDqaApproved && (
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                Approved from DQA
              </span>
            )}
          </div>
        </div>

        <div className="text-center py-8">
          <p className="text-gray-500 italic text-lg">
            No household mortality information available
          </p>
        </div>
      </div>
    );
  }

  const Err = (key) =>
    errors[key] ? (
      <p className="text-[0.9rem] text-red-600 mt-2">{errors[key]}</p>
    ) : null;

  const PersonErr = (personIndex, field) =>
    errors.demisedPersons?.[personIndex]?.[field] ? (
      <p className="text-[0.9rem] text-red-600 mt-2">
        {errors.demisedPersons[personIndex][field]}
      </p>
    ) : null;

  const canSubmit =
    !isDqaApproved &&
    mortality.anyDeath !== "" &&
    (mortality.anyDeath === "no" ||
      (mortality.deathCount &&
        mortality.demisedPersons.length > 0 &&
        mortality.demisedPersons.every(
          (person) =>
            person.gender &&
            person.ageOnDeath &&
            (person.gender !== "Female" || person.maternalDeath)
        )));

  return (
    <div className="bg-white">
      {/* <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          {isDqaApproved && (
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          )}
        </div>
      </div> */}
      <div className="flex justify-between items-center mb-3">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Moratality Report
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Moratality Report
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      <div className="space-y-6">
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-6">
            Mortality Status (Last 5 Years)
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First row: Any Death and Death Count */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Did any member of this household have died in last 5 years?
                </p>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="anyDeath"
                      value="yes"
                      checked={mortality.anyDeath === "yes"}
                      onChange={(e) => setField("anyDeath", e.target.value)}
                      disabled={isDqaApproved || isDqaRejected}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="anyDeath"
                      value="no"
                      checked={mortality.anyDeath === "no"}
                      onChange={(e) => setField("anyDeath", e.target.value)}
                      disabled={isDqaApproved || isDqaRejected}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
                {Err("anyDeath")}
              </div>

              {mortality.anyDeath === "yes" && (
                <div>
                  <ThinDropdownWithSearch
                    label="How many persons died in last 5 years?"
                    options={deathCountOptions}
                    value={mortality.deathCount}
                    onChange={(v) => setField("deathCount", v)}
                    required={false}
                    placeholder="Select count"
                    disabled={isDqaApproved || isDqaRejected}
                  />
                  {Err("deathCount")}
                </div>
              )}
            </div>

            {/* Dynamic person details */}
            {mortality.anyDeath === "yes" &&
              mortality.demisedPersons.length > 0 && (
                <div className="space-y-6">
                  {mortality.demisedPersons.map((person, index) => (
                    <div
                      key={index}
                      className="border border-gray-100 rounded-lg p-6 bg-gray-50"
                    >
                      <h4 className="text-md font-medium text-gray-700 mb-4">
                        Person {index + 1} Details
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Gender Selection */}
                        <div>
                          <p className="mb-2 text-sm font-medium text-gray-700">
                            What was the gender of demised person # {index + 1}?
                          </p>
                          <div className="flex items-center gap-6">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`gender-person-${index}`}
                                value="Male"
                                checked={person.gender === "Male"}
                                onChange={(e) =>
                                  setPersonField(
                                    index,
                                    "gender",
                                    e.target.value,
                                  )
                                }
                                disabled={isDqaApproved || isDqaRejected}
                                className="text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm text-gray-700">
                                Male
                              </span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`gender-person-${index}`}
                                value="Female"
                                checked={person.gender === "Female"}
                                onChange={(e) =>
                                  setPersonField(
                                    index,
                                    "gender",
                                    e.target.value,
                                  )
                                }
                                disabled={isDqaApproved || isDqaRejected}
                                className="text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm text-gray-700">
                                Female
                              </span>
                            </label>
                          </div>
                          {PersonErr(index, "gender")}
                        </div>

                        {/* Maternal Death - only show if female is selected */}
                        {person.gender === "Female" && (
                          <div>
                            <ThinDropdownWithSearch
                              label="Died during pregnancy or during child birth?"
                              options={vaccinationOptions}
                              value={person.maternalDeath}
                              onChange={(v) =>
                                setPersonField(index, "maternalDeath", v)
                              }
                              required={false}
                              placeholder="Select status"
                              disabled={isDqaApproved || isDqaRejected}
                            />
                            {PersonErr(index, "maternalDeath")}
                          </div>
                        )}

                        {/* Age on Death */}
                        <div>
                          <ThinTextField
                            label={`What was the age of demised person # ${
                              index + 1
                            }? `}
                            type="number"
                            value={person.ageOnDeath}
                            onChange={(e) =>
                              setPersonField(
                                index,
                                "ageOnDeath",
                                e.target.value,
                              )
                            }
                            disabled={isDqaApproved || isDqaRejected}
                            min="0"
                            max="120"
                          />
                          {PersonErr(index, "ageOnDeath")}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </form>
        </div>
      </div>

      {!isDqaApproved && !isDqaRejected && hasMortalityData && (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={updating || !canSubmit}
            className={`${
              updating || !canSubmit
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            } bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors`}
          >
            {updating ? "Processing..." : "Update"}
          </button>
        </div>
      )}
    </div>
  );
};

export default EditHouseholdDeath;